import { GlDatepicker, GlFormGroup, GlFormRadio } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { findStartAndDueDateWidget, newWorkItemId } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/work_items/utils.js?vue3';
import { s__, sprintf } from '~/locale';
import Tracking from '/builds/gitlab/gitlab-ee/app/assets/javascripts/tracking/index.js?vue3';
import { localeDateFormat, newDate, toISODateFormat } from '~/lib/utils/datetime_utility';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import { I18N_WORK_ITEM_ERROR_UPDATING, TRACKING_CATEGORY_SHOW, VIEW_CONTEXT, WIDGET_TYPE_START_AND_DUE_DATE } from '~/work_items/constants';
import updateWorkItemMutation from '~/work_items/graphql/update_work_item.mutation.graphql';
import WorkItemSidebarWidget from '/builds/gitlab/gitlab-ee/app/assets/javascripts/work_items/components/shared/work_item_sidebar_widget.vue?vue3';
const nullObjectDate = new Date(0);
const ROLLUP_TYPE_FIXED = 'fixed';
const ROLLUP_TYPE_INHERITED = 'inherited';
export default {
    name: 'WorkItemDates',
    dueDateInputId: 'due-date-input',
    startDateInputId: 'start-date-input',
    components: {
        GlDatepicker,
        GlFormGroup,
        GlFormRadio,
        WorkItemSidebarWidget
    },
    mixins: [
        glFeatureFlagsMixin(),
        Tracking.mixin()
    ],
    inject: {
        viewContext: {
            default: VIEW_CONTEXT.fullScreen
        }
    },
    props: {
        workItem: {
            type: Object,
            required: true
        },
        workItemType: {
            type: String,
            required: true
        },
        canUpdate: {
            type: Boolean,
            required: false,
            default: false
        },
        startDate: {
            type: String,
            required: false,
            default: null
        },
        dueDate: {
            type: String,
            required: false,
            default: null
        },
        isFixed: {
            type: Boolean,
            required: false,
            default: false
        },
        shouldRollUp: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'error',
        'update-widget-draft'
    ],
    data () {
        return {
            localDueDate: null,
            localStartDate: null,
            isUpdating: false,
            rollupType: null
        };
    },
    computed: {
        workItemId () {
            return this.workItem.id;
        },
        datesUnchanged () {
            const localDueDate = this.localDueDate || nullObjectDate;
            const localStartDate = this.localStartDate || nullObjectDate;
            const dueDate = this.dueDate ? newDate(this.dueDate) : nullObjectDate;
            const startDate = this.startDate ? newDate(this.startDate) : nullObjectDate;
            return localDueDate.getTime() === dueDate.getTime() && localStartDate.getTime() === startDate.getTime();
        },
        isDatepickerDisabled () {
            return !this.canUpdate || this.isUpdating;
        },
        // eslint-disable-next-line vue/no-unused-properties
        tracking () {
            return {
                category: TRACKING_CATEGORY_SHOW,
                label: 'item_rolledup_dates',
                property: `type_${this.workItemType}`,
                extra: {
                    viewContext: this.viewContext
                }
            };
        },
        startDateValue () {
            return this.startDate ? localeDateFormat.asDate.format(newDate(this.startDate)) : s__('WorkItem|None');
        },
        dueDateValue () {
            return this.dueDate ? localeDateFormat.asDate.format(newDate(this.dueDate)) : s__('WorkItem|None');
        },
        optimisticResponse () {
            const workItemDatesWidget = findStartAndDueDateWidget(this.workItem);
            const updatedDates = {
                dueDate: this.localDueDate ? toISODateFormat(this.localDueDate) : null,
                startDate: this.localStartDate ? toISODateFormat(this.localStartDate) : null
            };
            const editedFeatures = this.workItem?.features ? {
                features: {
                    ...this.workItem.features,
                    startAndDueDate: {
                        ...this.workItem.features.startAndDueDate || {},
                        ...updatedDates
                    }
                }
            } : {};
            return {
                workItemUpdate: {
                    errors: [],
                    workItem: {
                        ...this.workItem,
                        widgets: [
                            ...this.workItem.widgets.filter((widget)=>widget.type !== WIDGET_TYPE_START_AND_DUE_DATE),
                            {
                                ...workItemDatesWidget,
                                ...updatedDates
                            }
                        ],
                        ...editedFeatures
                    }
                }
            };
        }
    },
    watch: {
        dueDate: {
            handler (newDueDate) {
                this.localDueDate = newDate(newDueDate);
            },
            immediate: true
        },
        startDate: {
            handler (newStartDate) {
                this.localStartDate = newDate(newStartDate);
            },
            immediate: true
        },
        isFixed: {
            handler (isFixed) {
                this.rollupType = isFixed ? ROLLUP_TYPE_FIXED : ROLLUP_TYPE_INHERITED;
            },
            immediate: true
        }
    },
    methods: {
        clearDueDatePicker () {
            this.localDueDate = null;
        },
        clearStartDatePicker () {
            this.localStartDate = null;
        },
        handleStartDateInput () {
            if (this.localDueDate && this.localStartDate > this.localDueDate) {
                this.localDueDate = this.localStartDate;
            }
        },
        updateRollupType () {
            this.isUpdating = true;
            this.track('updated_rollup_type');
            if (this.workItemId === newWorkItemId(this.workItemType)) {
                this.$emit('update-widget-draft', {
                    rolledUpDates: {
                        isFixed: this.rollupType === ROLLUP_TYPE_FIXED,
                        rollUp: this.shouldRollUp
                    }
                });
                this.isUpdating = false;
                return;
            }
            this.$apollo.mutate({
                mutation: updateWorkItemMutation,
                variables: {
                    input: {
                        id: this.workItemId,
                        startAndDueDateWidget: {
                            isFixed: this.rollupType === ROLLUP_TYPE_FIXED
                        }
                    },
                    useWorkItemFeatures: Boolean(this.glFeatures?.workItemFeaturesField)
                },
                optimisticResponse: this.optimisticResponse
            }).then(({ data })=>{
                if (data.workItemUpdate.errors.length) {
                    throw new Error(data.workItemUpdate.errors.join('; '));
                }
            }).catch((error)=>{
                const message = sprintf(I18N_WORK_ITEM_ERROR_UPDATING, {
                    workItemType: this.workItemType
                });
                this.$emit('error', message);
                Sentry.captureException(error);
            }).finally(()=>{
                this.isUpdating = false;
            });
        },
        updateDates () {
            if (this.datesUnchanged) {
                return;
            }
            this.track('updated_dates');
            this.isUpdating = true;
            this.rollupType = ROLLUP_TYPE_FIXED;
            if (this.workItemId === newWorkItemId(this.workItemType)) {
                this.$emit('update-widget-draft', {
                    rolledUpDates: {
                        isFixed: true,
                        dueDate: this.localDueDate ? toISODateFormat(this.localDueDate) : null,
                        startDate: this.localStartDate ? toISODateFormat(this.localStartDate) : null,
                        rollUp: this.shouldRollUp
                    }
                });
                this.isUpdating = false;
                return;
            }
            this.$apollo.mutate({
                mutation: updateWorkItemMutation,
                variables: {
                    input: {
                        id: this.workItemId,
                        startAndDueDateWidget: {
                            isFixed: true,
                            dueDate: this.localDueDate ? toISODateFormat(this.localDueDate) : null,
                            startDate: this.localStartDate ? toISODateFormat(this.localStartDate) : null
                        }
                    },
                    useWorkItemFeatures: Boolean(this.glFeatures?.workItemFeaturesField)
                },
                optimisticResponse: this.optimisticResponse
            }).then(({ data })=>{
                if (data.workItemUpdate.errors.length) {
                    throw new Error(data.workItemUpdate.errors.join('; '));
                }
            }).catch((error)=>{
                const message = sprintf(I18N_WORK_ITEM_ERROR_UPDATING, {
                    workItemType: this.workItemType
                });
                this.$emit('error', message);
                Sentry.captureException(error);
            }).finally(()=>{
                this.isUpdating = false;
            });
        }
    }
};
